import { ShieldAlert, ShieldCheck, Activity, UserCheck } from "lucide-react";
import type { ClassificationV2, ConfidenceV2 } from "@/lib/api";

interface MetaStripProps {
  classification: ClassificationV2;
  confidence: ConfidenceV2;
  needsHumanReview: boolean;
  model: string;
  simulated: boolean;
}

const CONFIDENCE_COLORS: Record<
  ConfidenceV2["label"],
  { fg: string; bg: string; emoji: string }
> = {
  high: {
    fg: "var(--block-green)",
    bg: "color-mix(in oklch, var(--block-green) 14%, transparent)",
    emoji: "🟢",
  },
  medium: {
    fg: "var(--alert-orange)",
    bg: "color-mix(in oklch, var(--alert-orange) 14%, transparent)",
    emoji: "🟠",
  },
  low: {
    fg: "var(--block-red)",
    bg: "color-mix(in oklch, var(--block-red) 14%, transparent)",
    emoji: "🔴",
  },
};

const CRITICALITY_LABELS: Record<number, string> = {
  0: "Information générale",
  1: "Question technique",
  2: "Question procédurale",
  3: "Question contentieuse",
  4: "Risque critique",
};

/**
 * Bandeau méta consolidé : classification + confiance + revue humaine + modèle.
 *
 * Affiché sous le syllogisme pour qu'un juriste expérimenté puisse jauger
 * d'un coup d'œil la fiabilité de la réponse avant d'aller dans les
 * détails (claims, sources). Les couleurs reprennent la convention CdC #5.
 */
export function MetaStrip({
  classification,
  confidence,
  needsHumanReview,
  model,
  simulated,
}: MetaStripProps) {
  const cc = CONFIDENCE_COLORS[confidence.label];
  const critLabel =
    CRITICALITY_LABELS[classification.criticality] ??
    `Niveau ${classification.criticality}`;

  return (
    <div
      className="rounded-md border p-3 flex flex-wrap items-center gap-2 text-xs"
      style={{
        borderColor: "var(--graphite-600)",
        background: "var(--graphite-800)",
      }}
      aria-label="Métadonnées de la réponse"
    >
      <span
        className="flex items-center gap-1.5 px-2 py-1 rounded"
        style={{ background: cc.bg, color: cc.fg }}
        title={confidence.reasons.join(" — ")}
      >
        <Activity size={11} />
        Confiance {confidence.label} ({(confidence.score * 100).toFixed(0)}%)
      </span>
      <span
        className="flex items-center gap-1.5 px-2 py-1 rounded"
        style={{
          background:
            classification.criticality >= 3
              ? "color-mix(in oklch, var(--alert-orange) 14%, transparent)"
              : "var(--graphite-700)",
          color:
            classification.criticality >= 3
              ? "var(--alert-orange)"
              : "var(--arbitre-slate-300)",
        }}
      >
        <ShieldAlert size={11} />
        Criticité {classification.criticality} — {critLabel}
      </span>
      {needsHumanReview ? (
        <span
          className="flex items-center gap-1.5 px-2 py-1 rounded"
          style={{
            background:
              "color-mix(in oklch, var(--ai-violet) 14%, transparent)",
            color: "var(--ai-violet)",
          }}
        >
          <UserCheck size={11} />
          Validation humaine recommandée
        </span>
      ) : (
        <span
          className="flex items-center gap-1.5 px-2 py-1 rounded"
          style={{
            background: "var(--graphite-700)",
            color: "var(--arbitre-slate-500)",
          }}
        >
          <ShieldCheck size={11} />
          Pas de revue requise
        </span>
      )}
      <span
        className="ml-auto text-[10px]"
        style={{ color: "var(--arbitre-slate-500)" }}
      >
        Modèle :{" "}
        <code className="gsf-mono-proof text-[10px]">{model}</code>
        {simulated && (
          <span style={{ color: "var(--alert-orange)" }}> · simulation</span>
        )}
      </span>
    </div>
  );
}
